import { scaleLinear } from 'd3-scale'
import React, { FC, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { clamp } from 'Utils'
export interface Props {
  /** The linear color palette for the gradient */
  colorPalette: string[]
  /** The lowest value of the scale */
  minVal: number
  /** The highest value of the scale */
  maxVal: number
  /** The current value to point to on the scale */
  currentValue?: number
  /** The label for the current value */
  currentValueLabel?: string
  /** The number of discrete values to display. If no is provided it will be linear interpolated */
  numberOfValues?: number
  /** The number of ticks to display */
  numberOfTicks?: number
  /** Whether to show the tick lines */
  showTickLines?: boolean
  /** Whether to show the tick labels */
  showLabels?: boolean
  /** Show the current discrete value by giving it a border */
  markBorder?: boolean
  /** The custom labels for the discrete values */
  customLabels?: string[]
  /** The title of the legend */
  title?: string
  /** Whether to add a margin bottom to the legend */
  addMarginBottom?: boolean
}

const ColorLegend: FC<Props> = ({
  colorPalette,
  minVal,
  maxVal,
  currentValue,
  currentValueLabel,
  numberOfValues,
  numberOfTicks = 3,
  showTickLines = true,
  showLabels = true,
  markBorder = false,
  customLabels,
  title,
  addMarginBottom,
}): ReactElement => {
  const color = scaleLinear<string>()
    .domain([minVal, (minVal + maxVal) / 2, maxVal])
    .range(colorPalette || ['green', 'lightgrey', 'red'])
  const ticks = []
  const tickLabels = []
  const marginLeftOfCurrentValue = numberOfValues
    ? (100 / numberOfValues) * (currentValue ?? numberOfValues / 2) -
      100 / numberOfValues / 2 +
      '%'
    : clamp(
        currentValue !== undefined
          ? ((currentValue - minVal) / (maxVal - minVal)) * 100
          : 50,
        0,
        100
      ) + '%'

  for (let i = 0; i < numberOfTicks; i++) {
    ticks.push(<div className='tick' key={i} />)
  }
  for (
    let i = 0;
    i < numberOfTicks - (numberOfValues !== undefined ? 1 : 0);
    i++
  ) {
    tickLabels.push(
      <div className='tick-label' key={i}>
        {customLabels
          ? customLabels[i]
          : String(
              ((maxVal - minVal) / (numberOfTicks - 1)) * i + minVal
            ).replace('.', ',') + (currentValueLabel || '%')}
      </div>
    )
  }

  return (
    <ColorLegendWrapper
      descreteValues={numberOfValues !== undefined}
      addMarginBottom={addMarginBottom}
      showLabels={showLabels}
    >
      {title && <div>{title}</div>}
      {showLabels && <div id='tick-labels'>{tickLabels}</div>}
      {showTickLines && <div id='ticks'>{ticks}</div>}
      {!numberOfValues && (
        <div
          id='color-legend-bar'
          style={{
            backgroundImage:
              'linear-gradient(to right, ' +
              colorPalette.map((color) => color) +
              ')',
          }}
        />
      )}
      {numberOfValues && (
        <DescreteScale>
          {[...Array(numberOfValues)].map((el, i) => (
            <DiscreteColorElement
              key={i}
              color={color(i + minVal)}
              isActive={markBorder && i === (currentValue ?? 0) - minVal}
            />
          ))}
        </DescreteScale>
      )}
      {!markBorder && (
        <svg
          id='current-value'
          style={{
            marginLeft: marginLeftOfCurrentValue,
            opacity: currentValue !== undefined ? '1' : '0',
          }}
          width='88'
          viewBox='0 0 88 75'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path d='M44 0L87.3013 75H0.69873L44 0Z' />
        </svg>
      )}
    </ColorLegendWrapper>
  )
}
const DescreteScale = styled.div`
  display: flex;
  justify-content: space-between;
  /* height: 0.5em; */
  /* margin: 0.5em 0; */
`
const DiscreteColorElement = styled.div<{ color: string; isActive?: boolean }>`
  background: ${({ color }) => {
    return color
  }};
  height: 10px;
  flex-grow: 1;
  ${(props) =>
    props.isActive &&
    css`
      // animation of outline with
      @keyframes outlineAnimation {
        from {
          outline: 0px solid var(--text-color);
          outline-color: transparent;
          box-shadow: none;
        }
        to {
          outline: 3px solid var(--text-color);
        }
      }
      animation: outlineAnimation 0.2s ease-in-out;
      z-index: 1;
      outline: 3px solid var(--text-color);
      @media (max-width: 600px) {
        outline: 2px solid var(--text-color);
      }
      box-shadow: 0.4px 0.5px 2.2px rgba(0, 0, 0, 0.053),
        1.1px 1.3px 5.3px rgba(0, 0, 0, 0.077),
        2px 2.4px 10px rgba(0, 0, 0, 0.095),
        3.6px 4.2px 17.9px rgba(0, 0, 0, 0.113),
        6.7px 7.9px 33.4px rgba(0, 0, 0, 0.137),
        16px 19px 80px rgba(0, 0, 0, 0.19);
    `}
`
const ColorLegendWrapper = styled.div<{
  descreteValues?: boolean
  addMarginBottom?: boolean
  showLabels?: boolean
}>`
  --bar-height: 0.5em;
  font-family: var(--font-sans-serif);
  max-width: 670px;
  margin: 0 auto;
  margin-bottom: ${({ addMarginBottom }) => (addMarginBottom ? '3rem' : '0')};
  font-size: 0.75em;
  ${(props) =>
    !props.showLabels &&
    css`
    line-height: 0;
    `}
  @media(max-width: 600px) {
    /* margin-bottom: 1em; */
    font-size: 0.5em;
  }
  #color-legend-bar {
    height: var(--bar-height);
  }
  #ticks {
    display: flex;
    justify-content: space-between;
    .tick {
      // flex-grow: 1;
      height: 0.75em;
      border-left: 1px solid #888888;
    }
  }

  #tick-labels {
    text-align: center;
    display: flex;
    justify-content: space-between;
    /* @media only screen and (max-width: 600px) {
      margin-top: 2em;
    } */
    .tick-label {
      flex-grow: 1;
      width: 100%;
      // turn tick labels on mobile
    }
    ${(props) =>
      !props.descreteValues
        ? css`
            .tick-label:first-child {
              text-align: left;
            }
            .tick-label:last-child {
              text-align: right;
            }
          `
        : css`
            .tick-label {
              @media only screen and (max-width: 600px) {
                transform: rotate(-90deg) translatex(40%);
                text-align: left;
                // prevent line break
                white-space: nowrap;
              }
            }
          `}
  }
  #current-value {
    transform: translateX(-50%);
    height: 1em;
    transition: all 500ms cubic-bezier(0.77, 0, 0.175, 1);
    path {
      fill: var(--text-color);
    }
  }
`
export default ColorLegend
